import { tabelasDoClienteNoPedido } from '../../pedidos/tabelaFixa'
import { useEffect, useState } from 'react'
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { useLiveQuery } from 'dexie-react-hooks'
import { banco } from '../../db/banco'
import { buscarVivo } from '../../db/registroVivo'
import type { ItemPedido, SubPedido, TabelaPreco } from '../../db/tipos'
import { formatarMoeda } from '../../lib/formatos'
import {
  adicionarItem, atualizarItem, removerItem, interpretarQuantidade, atualizarItensDoRascunho,
} from '../../pedidos/carrinho'
import { avulsasDoItem, calcularTotalItem } from '../../pedidos/precos'
import { embalagemDoItem, pesoDaUnidadeNoNome } from '../../produtos/embalagem'
import { excluirPedidoEmCascata } from '../../pedidos/exclusao'
import { subPedidosDoAcrescimo, tabelaJaEnviada } from '../../pedidos/acrescimoOutraTabela'
import { textoDasDatasDoSubPedido } from '../../pedidos/datasDoSubPedido'
import { montarLinhasPreco, type LinhaPreco } from '../../pedidos/buscaPrecos'
import {
  textoDaRegra, quantidadeInicial, tabelaDoItem, avaliarQuantidade, avisoDaRegra, regraDoPreco,
} from '../../pedidos/regraQuantidade'
import { totaisPorMarca } from '../../pedidos/fechamento'
import AvisoClienteInativo from '../../componentes/AvisoClienteInativo'
import SeloDoPedido from '../../componentes/SeloDoPedido'
import { confirmar } from '../../componentes/confirmar'

// Aparencia nova, pedido dela em 17/09 (inspirada num mockup que ela mandou).
// SVG inline no mesmo estilo que CampoSenha.tsx ja usa neste projeto -- sem
// biblioteca de icones nova, so para esta tela.
function IconeCaixaVazia() {
  return (
    <svg viewBox="0 0 24 24" width="26" height="26" fill="none" stroke="currentColor"
      strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M21 8 12 3 3 8v8l9 5 9-5V8Z" />
      <path d="M3 8l9 5 9-5" />
      <path d="M12 13v8" />
    </svg>
  )
}

function IconeLixeira() {
  return (
    <svg viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="currentColor"
      strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M3 6h18" />
      <path d="M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
      <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
    </svg>
  )
}

function IconeTag() {
  return (
    <svg viewBox="0 0 24 24" width="11" height="11" fill="none" stroke="currentColor"
      strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M20.59 13.41 12 21.99 2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82Z" />
    </svg>
  )
}

function IconeCarrinho() {
  return (
    <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor"
      strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <circle cx="9" cy="21" r="1" />
      <circle cx="20" cy="21" r="1" />
      <path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6" />
    </svg>
  )
}

function IconeResumo() {
  return (
    <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor"
      strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <rect x="4" y="3" width="16" height="18" rx="2" />
      <line x1="8" y1="8" x2="16" y2="8" />
      <line x1="8" y1="13" x2="16" y2="13" />
      <line x1="8" y1="17" x2="12" y2="17" />
    </svg>
  )
}

function IconeFecharPedido() {
  return (
    <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor"
      strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8Z" />
      <path d="M14 2v6h6" />
      <line x1="12" y1="12" x2="12" y2="18" />
      <line x1="9" y1="15" x2="15" y2="15" />
    </svg>
  )
}

// Botao +/- de quantidade: soma na tela, mas passa pelo MESMO tratador do
// campo digitado (`aoAlterar`), entao o rascunho e a validacao de sempre
// continuam valendo -- clicar 3x em "+" e digitar "3" tem de dar o mesmo
// resultado.
function BotaoPasso({
  rotulo, aoClicar, ariaLabel,
}: {
  rotulo: '+' | '−'
  aoClicar: () => void
  ariaLabel: string
}) {
  return (
    <button
      type="button"
      onClick={aoClicar}
      aria-label={ariaLabel}
      className="w-7 h-7 shrink-0 rounded-md bg-teal-700 text-white font-bold leading-none flex items-center justify-center"
    >
      {rotulo}
    </button>
  )
}

// `acrescentando` = a tela de ACRESCENTAR a um pedido JA ENVIADO (pedido dela,
// 14/10): produto de outra tabela ou fornecedora vira um sub-pedido NOVO em
// rascunho, no mesmo pedido. O pedido segue 'fechado'; so o rascunho aparece e
// pode ser editado, e nao ha "excluir pedido" aqui.
export default function TelaPedido({ acrescentando = false }: { acrescentando?: boolean }) {
  const { id } = useParams()
  const navegar = useNavigate()
  const localizacao = useLocation()
  // O botao "Adicionar ao pedido" da ficha do produto chega aqui com
  // ?busca=<codigo> (decisao dela, 23/09): o vendedor so confirma a linha --
  // e a tabela -- com um toque, e o resto do fluxo e o de sempre.
  const [parametros] = useSearchParams()
  const [busca, setBusca] = useState(() => parametros.get('busca') ?? '')
  // Valor de quantidade recusado: antes deste conserto era descartado em
  // silencio (o mesmo bug que ela achou no "Ajustar itens").
  const [avisoQuantidade, setAvisoQuantidade] = useState<{ itemId: string; texto: string } | null>(
    null,
  )
  // O que esta ESCRITO na caixinha enquanto ela digita, por item. Sem isto os
  // dois campos da linha ficam comandados pelo valor gravado, e o React reimpoe
  // esse valor no DOM logo depois do onChange: o numero apagava e voltava na
  // mesma tecla, e so dava para trocar selecionando tudo por cima (pedido dela
  // em 17/08). E o mesmo remedio que o "Ajustar itens" ja usava.
  const [rascunhoQtd, setRascunhoQtd] = useState<Record<string, string>>({})
  const [rascunhoDesconto, setRascunhoDesconto] = useState<Record<string, string>>({})
  // O rascunho da terceira caixinha: as unidades SOLTAS. Mesmo remedio, mesmo
  // motivo -- sem ele o numero apaga e volta na mesma tecla.
  const [rascunhoAvulsa, setRascunhoAvulsa] = useState<Record<string, string>>({})

  const pedido = useLiveQuery(() => buscarVivo(banco.pedidos, id), [id])
  const cliente = useLiveQuery(
    () => (pedido ? banco.clientes.get(pedido.cliente_id) : undefined),
    [pedido?.cliente_id],
  )
  // Pedido dela 14/10: ao ABRIR o pedido, os itens ainda em rascunho acompanham o
  // cadastro corrigido (preco, embalagem). Roda uma vez por pedido aberto; sem
  // mudanca nao grava nada. Falha aqui nunca derruba a tela -- o item segue
  // como estava, que e o comportamento de antes.
  const pedidoAbertoId = pedido?.id
  const clienteDoPedidoId = pedido?.cliente_id
  useEffect(() => {
    if (!pedidoAbertoId || !clienteDoPedidoId) return
    atualizarItensDoRascunho(pedidoAbertoId, clienteDoPedidoId).catch(() => {})
  }, [pedidoAbertoId, clienteDoPedidoId])
  const representadas = useLiveQuery(() => banco.representadas.toArray(), [])
  const todosSubPedidos = useLiveQuery(
    () =>
      id
        ? banco.sub_pedidos
            .where('pedido_id')
            .equals(id)
            .filter((s) => s.apagado_em === null)
            .sortBy('letra')
        : Promise.resolve<SubPedido[]>([]),
    [id],
  )
  // Acrescentando a pedido enviado (pedido dela, 14/10): o vendedor ve o pedido
  // COMPLETO, igual ao carrinho; so que o que ja saiu do rascunho aparece sem
  // caixinhas (so leitura) -- mexer nele e pelo "Ajustar itens", que pede motivo.
  const subPedidos = todosSubPedidos
  const [avisoTabelaEnviada, setAvisoTabelaEnviada] = useState<string | null>(null)
  const itens = useLiveQuery(async () => {
    if (!subPedidos) return [] as ItemPedido[]
    const ids = subPedidos.map((s) => s.id)
    return banco.itens_pedido
      .where('sub_pedido_id')
      .anyOf(ids)
      .filter((i) => i.apagado_em === null)
      .toArray()
  }, [subPedidos])
  const todosProdutos = useLiveQuery(() => banco.produtos.toArray(), [])
  const tabelas = useLiveQuery(
    () =>
      banco.tabelas_preco
        .filter((t) => t.apagado_em === null && t.ativa)
        .sortBy('nome'),
    [],
  ) as TabelaPreco[] | undefined

  const precos = useLiveQuery(
    () => banco.precos.filter((p) => p.apagado_em === null).toArray(),
    [],
  )
  const vinculosDoCliente = useLiveQuery(
    () =>
      pedido
        ? banco.clientes_representadas
            .where('cliente_id').equals(pedido.cliente_id)
            .filter((v) => v.apagado_em === null)
            .toArray()
        : Promise.resolve<import('../../db/tipos').ClienteRepresentada[]>([]),
    [pedido?.cliente_id],
  )

  const tabelasPreferidas = new Set(
    (vinculosDoCliente ?? [])
      .map((v) => v.tabela_preco_padrao_id)
      .filter((id): id is string => id !== null),
  )
  const linhasBusca = montarLinhasPreco(
    todosProdutos ?? [], tabelasDoClienteNoPedido(tabelas ?? [], vinculosDoCliente ?? []),
    precos ?? [], busca, tabelasPreferidas,
  )

  const nomeRepresentada = (idRep: string) =>
    representadas?.find((r) => r.id === idRep)?.nome ?? ''
  const nomeProduto = (idProd: string) =>
    todosProdutos?.find((p) => p.id === idProd)?.descricao ?? ''

  // Pedido dela 14/10: frete e condicao de pagamento sao perguntados SO no
  // fechamento (antes o app perguntava no primeiro item e de novo no fim). O item
  // entra sem os dois; o imposto informativo e refeito ao fechar.
  async function confirmarAdicao(linha: LinhaPreco) {
    if (!pedido || !cliente) return
    // ⚠ Antes era `1` fixo. Nascer no minimo poupa digitacao na rua e evita
    // que o aviso amarelo apareca no instante em que a pessoa poe o produto:
    // com o minimo ja no campo, o aviso so aparece se ela BAIXAR dele -- que e
    // quando ha de fato algo a dizer. Quem decide e o chamador; `adicionarItem`
    // segue recebendo a quantidade pronta.
    const r = await adicionarItem(
      pedido.id, cliente.id, linha.produto, linha.tabela.id, quantidadeInicial(linha.regra),
      // Nasce com ZERO unidades soltas e com o PRECO delas ja gravado. O preco
      // vem agora porque e agora que a linha de preco esta na mao: sem ele, a
      // caixinha das soltas apareceria mostrando R$ 0,00 e a primeira digitacao
      // gravaria "6 soltas sem preco" -- estado que o banco recusa no push.
      { quantidade: 0, preco: linha.precoUnidade ?? 0 },
    )
    if (r.sucesso) setBusca('')
    // r.sucesso === false (sem_preco) so ocorreria se o preco sumisse entre a
    // busca e o clique; nesse caso nao adiciona nada e a lista se atualiza sozinha.
  }

  async function aoAdicionar(linha: LinhaPreco) {
    if (!pedido || !cliente) return
    setAvisoTabelaEnviada(null)
    if (
      acrescentando
      && tabelaJaEnviada(todosSubPedidos ?? [], linha.produto.representada_id, linha.tabela.id)
    ) {
      setAvisoTabelaEnviada(
        'Esta fornecedora e tabela já têm sub-pedido neste pedido. Para acrescentar nela, '
        + 'use “Ajustar itens” no detalhe do pedido; aqui entram produtos de outra tabela ou fornecedora.',
      )
      return
    }
    await confirmarAdicao(linha)
  }

  // ▶ Os tratadores da linha do carrinho. Sairam de dentro do JSX quando a
  // linha passou a ter DOIS ramos (com e sem embalagem fechada): duplicar as
  // regras do rascunho e do zero em dois ramos e garantir que um dia elas
  // divirjam. Nenhuma regra mudou na mudanca de lugar.
  function limparRascunho(
    set: React.Dispatch<React.SetStateAction<Record<string, string>>>,
    itemId: string,
  ) {
    // Saiu do campo: quem manda volta a ser o valor gravado. Deixou em branco?
    // A linha volta a mostrar o que esta no pedido -- em branco nao e
    // quantidade nenhuma.
    set((a) => {
      const semEste = { ...a }
      delete semEste[itemId]
      return semEste
    })
  }

  function aoDigitarQuantidade(item: ItemPedido, valor: string) {
    // O rascunho e a fonte enquanto ela digita: campo vazio continua vazio ate
    // ela sair dele.
    setRascunhoQtd((a) => ({ ...a, [item.id]: valor }))
    // Aqui a mudanca vale A CADA TECLA. Por isso 0 NAO apaga o item como no
    // "Ajustar itens": quem digitasse 0 antes do 5 perderia o produto. Zero
    // aponta o "remover" da propria linha -- o que nao pode voltar a acontecer
    // e o silencio.
    if (valor.trim() === '') {
      setAvisoQuantidade(null) // campo em branco: ela esta digitando
      return
    }
    // ⚠ A outra dimensao entra na conta: com unidades soltas na linha, zerar as
    // caixas e uma VENDA ("so 6 unidades"), nao um pedido de remocao.
    const acao = interpretarQuantidade(valor, item.quantidade, avulsasDoItem(item).quantidade)
    if (acao.acao === 'quantidade') {
      setAvisoQuantidade(null)
      void atualizarItem(item, { quantidade: acao.nova })
      return
    }
    setAvisoQuantidade({
      itemId: item.id,
      texto:
        acao.acao === 'remover'
          ? 'Para tirar este produto, use o “remover” ao lado.'
          : acao.aviso,
    })
  }

  // As unidades SOLTAS. Mesma regra do campo de cima, com a simetria exata: o
  // zero aqui so aponta o "remover" quando NAO ha caixa nenhuma na linha.
  function aoDigitarAvulsa(item: ItemPedido, precoUnidade: number | null, valor: string) {
    setRascunhoAvulsa((a) => ({ ...a, [item.id]: valor }))
    if (valor.trim() === '') {
      setAvisoQuantidade(null)
      return
    }
    const acao = interpretarQuantidade(valor, avulsasDoItem(item).quantidade, item.quantidade)
    if (acao.acao === 'quantidade') {
      setAvisoQuantidade(null)
      // O preco viaja junto com a quantidade: a linha de preco de hoje e quem
      // manda, e gravar quantidade sem preco e o estado que o banco recusa.
      void atualizarItem(item, {
        quantidade_avulsa: acao.nova,
        ...(precoUnidade !== null ? { preco_avulso: precoUnidade } : {}),
      })
      return
    }
    setAvisoQuantidade({
      itemId: item.id,
      texto:
        acao.acao === 'remover'
          ? 'Para tirar este produto, use o “remover” ao lado.'
          : acao.aviso,
    })
  }

  // Os botoes +/- (pedido dela em 17/09): leem o valor que esta na tela AGORA
  // (rascunho, se ela estiver digitando; senao o gravado) e mandam pelo MESMO
  // caminho de quem digita, para as duas formas nunca divergirem.
  function passoQuantidade(item: ItemPedido, delta: number) {
    const atual = Number(rascunhoQtd[item.id] ?? item.quantidade)
    aoDigitarQuantidade(item, String(Math.max(0, atual + delta)))
  }

  function passoAvulsa(item: ItemPedido, precoUnidade: number | null, delta: number) {
    const atual = Number(rascunhoAvulsa[item.id] ?? avulsasDoItem(item).quantidade)
    aoDigitarAvulsa(item, precoUnidade, String(Math.max(0, atual + delta)))
  }

  function aoDigitarDesconto(item: ItemPedido, valor: string) {
    setRascunhoDesconto((a) => ({ ...a, [item.id]: valor }))
    // Campo em branco NAO e desconto zero: antes disto apagar virava
    // Number('') === 0, GRAVAVA desconto 0 e o zero voltava na tela. Zero so
    // vale se ela digitar zero.
    if (valor.trim() === '') return
    void atualizarItem(item, { desconto_pct: Number(valor) })
  }

  // ▶ A linha de HOJE, do produto que nao vem em embalagem fechada. Recortada
  // sem alterar uma virgula quando a linha ganhou o segundo ramo: e assim que a
  // garantia "produto sem fator nao muda" fica ESTRUTURAL, em vez de ser uma
  // torcida. E funcao, e nao componente, de proposito -- sem props e sem hooks,
  // nada pode divergir do que estava escrito aqui dentro do `map`.
  function linhaSimples(item: ItemPedido) {
    return (
      /* ⚠ `flex-wrap` nao e enfeite. Esta linha tem dois campos de largura fixa
         (w-20 e w-16), o "x", o preco, o selo da tabela, o "%" e o total:
         somados dao ~393px, que NAO cabe num celular de 360px nem de 390px.
         Sem poder quebrar, o total saia da tela -- e como a pagina nao rola de
         lado, o representante simplesmente nao via quanto deu a linha. Medido
         em producao em 15/08: transbordava 49px em 360px. Ha E2E cobrando
         (pedido-cabe-no-celular.spec.ts). */
      <div className="flex flex-wrap items-center gap-2 mt-1">
        {/* ⚠ O rotulo do botao NUNCA pode conter "Quantidade de X" por inteiro:
            o Playwright acha rotulo por SUBSTRING, e um teste que procura
            `getByLabel('Quantidade de X')` acharia os 3 (input + os 2
            botoes) -- foi exatamente o que quebrou 4 E2E na primeira versao
            disto. Por isso "−1 unidade", nunca "Diminuir quantidade". */}
        <BotaoPasso
          rotulo="−"
          ariaLabel={`−1 unidade: ${nomeProduto(item.produto_id)}`}
          aoClicar={() => passoQuantidade(item, -1)}
        />
        <input
          // Rotulo COM o nome do produto: a lista de itens vem de um `anyOf` do
          // Dexie, entao a ordem das linhas nao e a ordem em que foram
          // adicionadas -- apontar a caixinha por posicao apontaria para a
          // linha errada.
          aria-label={`Quantidade de ${nomeProduto(item.produto_id)}`}
          type="number" min="0.001" step="any"
          value={rascunhoQtd[item.id] ?? String(item.quantidade)}
          onChange={(e) => aoDigitarQuantidade(item, e.target.value)}
          onBlur={() => limparRascunho(setRascunhoQtd, item.id)}
          className="w-14 border border-slate-300 rounded px-2 py-1 text-right"
        />
        <BotaoPasso
          rotulo="+"
          ariaLabel={`+1 unidade: ${nomeProduto(item.produto_id)}`}
          aoClicar={() => passoQuantidade(item, 1)}
        />
        <span className="text-slate-500">×</span>
        <span className="text-slate-700">{formatarMoeda(item.preco_unitario)}</span>
        <span className="text-xs bg-slate-100 text-slate-500 rounded px-1">
          {rotuloOrigem(item)}
        </span>
        <input
          aria-label={`Desconto % de ${nomeProduto(item.produto_id)}`}
          type="number" min="0" max="100" step="0.5"
          value={rascunhoDesconto[item.id] ?? String(item.desconto_pct)}
          onChange={(e) => aoDigitarDesconto(item, e.target.value)}
          onBlur={() => limparRascunho(setRascunhoDesconto, item.id)}
          className="w-12 border border-slate-300 rounded px-2 py-1 text-right"
          title="Desconto %"
        />
        <span className="text-xs text-slate-400">% desc</span>
        <span className="ml-auto font-medium text-slate-800">
          {formatarMoeda(item.total_item)}
        </span>
      </div>
    )
  }

  function rotuloOrigem(item: ItemPedido): string {
    if (item.origem_preco === 'negociado') return 'negociado'
    if (item.origem_preco === 'manual') return 'manual'
    return 'tabela'
  }

  const totalGeral = (subPedidos ?? []).reduce((soma, s) => soma + s.valor_total, 0)

  const minimosPorMarca = totaisPorMarca(
    subPedidos ?? [],
    new Map((representadas ?? []).map((r) => [r.id, r] as const)),
  )

  // key === 'default' marca a PRIMEIRA entrada da sessao (aba aberta direto no
  // link do pedido): nao ha para onde voltar dentro do app. Verificado na lib:
  // createBrowserHistory monta a location inicial com
  // `globalHistory.state?.key || "default"`, e toda navegacao interna faz push
  // com chave gerada. window.history.length nao serve -- conta a aba inteira,
  // inclusive paginas de fora do app.
  function aoVoltar() {
    if (acrescentando && pedido) {
      navegar(`/pedidos/${pedido.id}`, { replace: true })
      return
    }
    if (localizacao.key === 'default') navegar('/pedidos')
    else navegar(-1)
  }

  async function aoExcluir() {
    if (!id) return
    if (!(await confirmar('Excluir este pedido?', { botao: 'Excluir', perigo: true }))) return
    await excluirPedidoEmCascata(id)
    navegar('/pedidos', { replace: true })
  }

  if (!pedido || (!acrescentando && pedido.status !== 'aberto')) {
    return <p className="p-4 text-sm text-slate-500">Carregando pedido…</p>
  }
  // So 'fechado' (ja saiu do carrinho) pode receber acrescimo por esta tela.
  if (acrescentando && pedido.status !== 'fechado') {
    return <p className="p-4 text-sm text-slate-500">Carregando pedido…</p>
  }

  return (
    <section className="p-4 space-y-4">
      {/* Card, e nao mais uma segunda barra escura: o app JA tem uma barra
          teal fixa no topo (Layout.tsx, com o voltar geral e a sincronizacao).
          Empilhar outra igual aqui pesaria a tela -- o toque de "profissional"
          do exemplo dela vem do card e dos icones, nao de duplicar a cor. */}
      <div className="bg-white rounded-xl shadow-sm p-3 flex items-start justify-between gap-2">
        <div className="min-w-0">
          <button
            type="button"
            onClick={aoVoltar}
            className="text-teal-700 text-sm underline mb-1"
          >
            ← Voltar
          </button>
          <div className="flex items-center gap-2">
            <span className="text-teal-700 shrink-0"><IconeCarrinho /></span>
            <h2 className="text-lg font-bold text-slate-800 truncate">
              {acrescentando ? `Acrescentar ao pedido ${pedido.numero}` : `Pedido ${pedido.numero}`}
            </h2>
          </div>
          <p className="text-sm text-slate-500">{cliente?.nome_fantasia || cliente?.razao_social}</p>
          {cliente && <AvisoClienteInativo cliente={cliente} temPedido />}
          <SeloDoPedido pedidoId={pedido.id} />
        </div>
        {/* So existe no pedido ABERTO: PedidoRoteador manda fechado/cancelado
            para DetalhePedido, que nao renderiza este cabecalho. Pedido fechado
            ja tem numeracao, PDF na fabrica e comissao -- la o caminho e cancelar. */}
        {!acrescentando && (
          <button
            type="button"
            onClick={() => void aoExcluir()}
            className="text-red-600 text-sm underline shrink-0"
          >
            Excluir pedido
          </button>
        )}
      </div>
      {acrescentando && (
        <p className="text-sm text-teal-900 bg-teal-50 rounded-lg px-3 py-2">
          Este pedido já foi enviado e não muda. Os produtos que você acrescentar aqui, de outra
          tabela ou fornecedora, formam um sub-pedido novo, com o PDF dele.
        </p>
      )}
      {avisoTabelaEnviada && (
        <p role="alert" className="text-sm text-amber-800 bg-amber-50 rounded-lg px-3 py-2">
          {avisoTabelaEnviada}
        </p>
      )}

      <div className="relative">
        <input
          value={busca}
          onChange={(e) => setBusca(e.target.value)}
          placeholder="Buscar produto para adicionar (mín. 2 letras)"
          className="w-full border border-slate-300 rounded-lg px-3 py-2"
        />
        {linhasBusca.length > 0 && (
          <ul className="absolute z-10 inset-x-0 bg-white border border-slate-200 rounded-lg shadow-lg divide-y max-h-64 overflow-y-auto">
            {linhasBusca.map((l) => (
              <li key={`${l.tabela.id}:${l.produto.id}`}>
                <button
                  onClick={() => void aoAdicionar(l)}
                  className="w-full text-left px-3 py-2 text-sm hover:bg-slate-50"
                >
                  <span className="font-medium">{l.produto.descricao}</span>
                  <span className="block text-xs text-slate-500">
                    {l.produto.codigo} — {nomeRepresentada(l.produto.representada_id)} · {l.tabela.nome} · {formatarMoeda(l.preco)}
                    {/* A etiqueta so aparece quando o preco TEM regra. E o unico
                        ponto da tela que evita o erro em vez de relatar depois:
                        ele mostra o minimo ANTES de a pessoa escolher a tabela. */}
                    {textoDaRegra(l.regra) !== null && (
                      <span className="text-amber-700"> · {textoDaRegra(l.regra)}</span>
                    )}
                  </span>
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>

      {subPedidos?.map((subPedido) => {
        const itensDoSub = (itens ?? []).filter((i) => i.sub_pedido_id === subPedido.id)
        const semPrecoDaTabela = itensDoSub.filter((i) => i.origem_preco === 'manual').length
        const nomeTabela = tabelas?.find((t) => t.id === subPedido.tabela_preco_id)?.nome ?? null
        // Pedido dela em 17/09: o minimo da marca desce para o FIM do card
        // daquela marca, em vez de um bloco a parte juntando todas as marcas
        // no fim da tela -- fica junto do que ele mede, mais facil de ligar
        // um ao outro.
        const minimoDaMarca = minimosPorMarca.find(
          (m) => m.representadaId === subPedido.representada_id,
        )
        if (acrescentando && subPedido.status !== 'rascunho') {
          return (
            <div key={subPedido.id} className="bg-white rounded-xl shadow-sm p-3 space-y-1">
              <div className="flex items-center justify-between gap-2">
                <h3 className="font-medium text-slate-800">
                  {subPedido.letra} — {nomeRepresentada(subPedido.representada_id)}
                  {nomeTabela ? ` · ${nomeTabela}` : ''}
                </h3>
                <span className="text-sm font-bold text-slate-700">
                  {formatarMoeda(subPedido.valor_total)}
                </span>
              </div>
              {textoDasDatasDoSubPedido(subPedido) && (
                <p className="text-xs text-slate-500">{textoDasDatasDoSubPedido(subPedido)}</p>
              )}
              <p className="text-xs text-teal-800">
                Já enviado — para mudar, use “Ajustar itens” no detalhe do pedido.
              </p>
              {itensDoSub.map((item) => (
                <div key={item.id} className="flex justify-between gap-2 border-t border-slate-100 pt-1 text-sm text-slate-700">
                  <span className="min-w-0">{nomeProduto(item.produto_id)} · {item.quantidade}</span>
                  <span className="shrink-0">{formatarMoeda(item.total_item)}</span>
                </div>
              ))}
            </div>
          )
        }
        return (
          <div key={subPedido.id} className="bg-white rounded-xl shadow-sm p-3 space-y-2">
            <div className="flex items-center justify-between">
              <h3 className="font-medium text-slate-800">
                {subPedido.letra} — {nomeRepresentada(subPedido.representada_id)}
                {nomeTabela ? ` · ${nomeTabela}` : ''}
              </h3>
              <span className="text-sm font-bold text-slate-700">
                {formatarMoeda(subPedido.valor_total)}
              </span>
            </div>
            {semPrecoDaTabela > 0 && (
              <p className="text-xs text-amber-700 bg-amber-50 rounded px-2 py-1">
                {semPrecoDaTabela} {semPrecoDaTabela === 1 ? 'item está' : 'itens estão'} com preço
                manual — não {semPrecoDaTabela === 1 ? 'vem' : 'vêm'} da tabela
                {nomeTabela ? ` ${nomeTabela}` : ''}. Confira.
              </p>
            )}
            {itensDoSub.map((item) => {
              // A regra sai da linha de `precos` da TABELA DAQUELE ITEM: o
              // mesmo produto tem minimos diferentes em cada tabela, e pegar
              // o primeiro preco do produto mostraria o numero da tabela errada.
              const tabelaId = tabelaDoItem(item.origem_preco)
              const precoDoItem = tabelaId
                ? (precos ?? []).find(
                    (p) => p.tabela_preco_id === tabelaId && p.produto_id === item.produto_id,
                  )
                : undefined
              const avisoRegra = precoDoItem
                ? avisoDaRegra(avaliarQuantidade(item.quantidade, regraDoPreco(precoDoItem)))
                : null
              // ⚠ `embalagemDoItem` e nao `embalagemDoProduto`: o fator sai do
              // ITEM VENDIDO, congelado no dia da venda. Se a fabrica trocar a
              // caixa de 20 para 24, um pedido antigo nao pode se reescrever.
              const produtoDoItem = todosProdutos?.find((p) => p.id === item.produto_id)
              const embalagem = embalagemDoItem(item, produtoDoItem ?? {})
              // Pedido dela em 17/09: mostrar o peso da unidade (ex.: "12X400G")
              // quando o proprio nome do produto traz essa informacao, em vez de
              // so "(12 un)". Sem peso achado, cai no texto de sempre.
              const pesoUnidade = embalagem.fator !== null
                ? pesoDaUnidadeNoNome(embalagem.fator, produtoDoItem?.descricao ?? '')
                : null
              const avulsas = avulsasDoItem(item)
              // Esta fabrica quebra a embalagem? Nulo = nao, e entao a caixinha
              // das soltas nem aparece.
              const vendeAvulso = (precoDoItem?.preco_unidade ?? null) !== null
              return (
              <div key={item.id} className="border-t border-slate-100 pt-2 text-sm">
                <div className="flex items-start gap-2">
                  {/* Foto do produto quando tiver; a maioria hoje nao tem, entao
                      o icone generico e o caso comum, nao a excecao (pedido
                      dela, 17/09 -- inspirado num mockup). */}
                  <div className="w-12 h-12 shrink-0 rounded-lg bg-slate-100 text-slate-400 flex items-center justify-center overflow-hidden">
                    {produtoDoItem?.foto_url ? (
                      <img src={produtoDoItem.foto_url} alt="" className="w-full h-full object-cover" />
                    ) : (
                      <IconeCaixaVazia />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-2">
                      <span className="font-semibold text-slate-800 leading-snug">
                        {nomeProduto(item.produto_id)}
                      </span>
                      {/* Pedido dela em 17/09: o valor AQUI EM CIMA e o CHEIO, sem
                          desconto -- fixo, so muda com quantidade. Quem muda com o
                          desconto e o total la embaixo, perto da caixinha de %. As
                          duas juntas deixam o vendedor VER a diferenca, em vez de
                          um numero so que muda e apaga o "antes". Risca quando ha
                          desconto de verdade, pra ficar claro que este NAO e o
                          valor final. */}
                      <span
                        className={
                          item.desconto_pct > 0
                            ? 'text-slate-400 line-through shrink-0'
                            : 'font-semibold text-teal-700 shrink-0'
                        }
                      >
                        {formatarMoeda(calcularTotalItem(item.quantidade, item.preco_unitario, 0, avulsas))}
                      </span>
                    </div>
                    <div className="flex flex-wrap items-center gap-1.5 text-xs text-slate-500 mt-0.5">
                      {produtoDoItem?.codigo && <span>Código: {produtoDoItem.codigo}</span>}
                      {(produtoDoItem?.categoria || produtoDoItem?.linha) && (
                        <span className="inline-flex items-center gap-1 bg-teal-50 text-teal-700 rounded-full px-2 py-0.5">
                          <IconeTag /> {produtoDoItem?.categoria ?? produtoDoItem?.linha}
                        </span>
                      )}
                    </div>
                    <button
                      onClick={() => void removerItem(item)}
                      className="inline-flex items-center gap-1 text-red-500 text-xs mt-1"
                    >
                      <IconeLixeira /> Remover
                    </button>
                  </div>
                </div>
                {embalagem.fator === null ? (
                  /* ⛔ O RAMO DE HOJE, SEM UMA VIRGULA DE DIFERENCA. 1.992 dos
                     2.287 produtos do banco caem aqui, e e este bloco que o
                     `pedido-cabe-no-celular.spec.ts` mede. */
                  linhaSimples(item)
                ) : (
                  /* O produto vem em embalagem fechada: a linha usa DUAS ou tres
                     alturas em vez de uma. Nao ha como espremer um segundo campo
                     de quantidade e um segundo preco na linha unica -- ela ja
                     transbordava 49px em 360px so com um. */
                  <div className="mt-1 space-y-1">
                    {/* A embalagem fechada. Some quando ela zera as caixas e ha
                        unidades soltas: "0 CX" nao e informacao para quem esta
                        vendendo (decisao dela, 02/09). A condicao e a MESMA de
                        textoDaEmbalagem(), que escreve a frase do PDF -- as duas
                        leem a mesma lei para nao divergirem. */}
                    {(item.quantidade > 0 || avulsas.quantidade === 0) && (
                      <div className="flex flex-wrap items-center gap-2">
                        <BotaoPasso
                          rotulo="−"
                          ariaLabel={`−1 caixa: ${nomeProduto(item.produto_id)}`}
                          aoClicar={() => passoQuantidade(item, -1)}
                        />
                        <input
                          aria-label={`Quantidade de ${nomeProduto(item.produto_id)}`}
                          type="number" min="0" step="any" inputMode="numeric"
                          value={rascunhoQtd[item.id] ?? String(item.quantidade)}
                          onChange={(e) => aoDigitarQuantidade(item, e.target.value)}
                          onBlur={() => limparRascunho(setRascunhoQtd, item.id)}
                          className="w-14 border border-slate-300 rounded px-2 py-1 text-right"
                        />
                        <BotaoPasso
                          rotulo="+"
                          ariaLabel={`+1 caixa: ${nomeProduto(item.produto_id)}`}
                          aoClicar={() => passoQuantidade(item, 1)}
                        />
                        {/* O "(20 un)" e o que o cliente vai conferir na entrega:
                            sem ele "3 CX" nao diz quantos bombons chegaram. Com
                            peso achado no nome, "(12X400G)" diz mais ainda. */}
                        <span className="text-slate-500">
                          {embalagem.nome} ({pesoUnidade ? `${embalagem.fator}X${pesoUnidade}` : `${embalagem.fator} un`}) ×
                        </span>
                        <span className="text-slate-700">{formatarMoeda(item.preco_unitario)}</span>
                        <span className="text-xs bg-slate-100 text-slate-500 rounded px-1">
                          {rotuloOrigem(item)}
                        </span>
                      </div>
                    )}

                    {/* As unidades soltas. Some por completo quando a fabrica nao
                        quebra a embalagem -- campo que nao serve para nada so
                        atrapalha na rua. */}
                    {vendeAvulso && (
                      <div className="flex flex-wrap items-center gap-2">
                        <BotaoPasso
                          rotulo="−"
                          ariaLabel={`−1 avulsa: ${nomeProduto(item.produto_id)}`}
                          aoClicar={() => passoAvulsa(item, precoDoItem?.preco_unidade ?? null, -1)}
                        />
                        <input
                          aria-label={`Unidades avulsas de ${nomeProduto(item.produto_id)}`}
                          type="number" min="0" step="any" inputMode="numeric"
                          value={rascunhoAvulsa[item.id] ?? String(avulsas.quantidade)}
                          onChange={(e) =>
                            aoDigitarAvulsa(item, precoDoItem?.preco_unidade ?? null, e.target.value)
                          }
                          onBlur={() => limparRascunho(setRascunhoAvulsa, item.id)}
                          className="w-14 border border-slate-300 rounded px-2 py-1 text-right"
                        />
                        <BotaoPasso
                          rotulo="+"
                          ariaLabel={`+1 avulsa: ${nomeProduto(item.produto_id)}`}
                          aoClicar={() => passoAvulsa(item, precoDoItem?.preco_unidade ?? null, 1)}
                        />
                        <span className="text-slate-500">un ×</span>
                        <span className="text-slate-700">{formatarMoeda(avulsas.preco)}</span>
                      </div>
                    )}

                    {/* O desconto e o total descem para a sua propria linha: as
                        de cima ja tomam a largura, e o total NAO PODE sair da
                        tela -- foi exatamente isso que aconteceu em 15/08 e o
                        representante ficou sem saber quanto deu a linha. */}
                    <div className="flex flex-wrap items-center gap-2">
                      <input
                        aria-label={`Desconto % de ${nomeProduto(item.produto_id)}`}
                        type="number" min="0" max="100" step="0.5"
                        value={rascunhoDesconto[item.id] ?? String(item.desconto_pct)}
                        onChange={(e) => aoDigitarDesconto(item, e.target.value)}
                        onBlur={() => limparRascunho(setRascunhoDesconto, item.id)}
                        className="w-12 border border-slate-300 rounded px-2 py-1 text-right"
                        title="Desconto %"
                      />
                      <span className="text-xs text-slate-400">% desc</span>
                      <span className="ml-auto font-medium text-slate-800">
                        {formatarMoeda(item.total_item)}
                      </span>
                    </div>
                  </div>
                )}
                {avisoQuantidade?.itemId === item.id && (
                  <p className="text-xs text-red-600 mt-1">{avisoQuantidade.texto}</p>
                )}
                {avisoRegra && (
                  // ⚠ AMBAR, e nao vermelho: nao e erro de quem digitou. E a
                  // condicao da fabrica, e vender fora dela e decisao legitima
                  // que a fornecedora pode aceitar por telefone.
                  <p className="text-xs text-amber-700 mt-1">{avisoRegra}</p>
                )}
              </div>
              )
            })}
            {/* O minimo desta marca, no PE do card dela -- ver comentario onde
                `minimoDaMarca` e calculado. */}
            {minimoDaMarca && (
              <div className="pt-2 border-t border-slate-100">
                <div className="flex items-center justify-between text-xs text-slate-600">
                  <span>Mínimo da fornecedora</span>
                  <span>
                    {formatarMoeda(minimoDaMarca.total)} / {formatarMoeda(minimoDaMarca.minimo)}
                  </span>
                </div>
                <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden mt-0.5">
                  <div
                    className={minimoDaMarca.faltam === 0 ? 'h-full bg-emerald-500' : 'h-full bg-amber-400'}
                    style={{ width: `${Math.min(100, (minimoDaMarca.total / minimoDaMarca.minimo) * 100)}%` }}
                  />
                </div>
                {minimoDaMarca.faltam > 0 && (
                  <p className="text-xs text-amber-600 mt-0.5">
                    Faltam {formatarMoeda(minimoDaMarca.faltam)} para o mínimo
                  </p>
                )}
              </div>
            )}
          </div>
        )
      })}

      {subPedidos && subPedidos.length > 0 && (
        <div className="bg-teal-50 rounded-xl p-3 flex items-center gap-3">
          <span className="w-9 h-9 shrink-0 rounded-lg bg-teal-700 text-white flex items-center justify-center">
            <IconeResumo />
          </span>
          <div className="min-w-0">
            <span className="block text-xs text-teal-700">Total do pedido</span>
            <span className="block text-lg font-bold text-slate-800">{formatarMoeda(totalGeral)}</span>
          </div>
        </div>
      )}

      <button
        onClick={() => navegar(`/pedidos/${pedido.id}/fechar`)}
        disabled={
          !itens
          || (acrescentando
            ? !itens.some((i) => subPedidosDoAcrescimo(subPedidos ?? []).some((s) => s.id === i.sub_pedido_id))
            : itens.length === 0)
        }
        className="w-full bg-teal-700 text-white rounded-lg py-3 font-medium disabled:opacity-50 flex items-center justify-center gap-2"
      >
        <IconeFecharPedido />
        {acrescentando ? 'Fechar o sub-pedido novo' : 'Fechar pedido'}
      </button>

    </section>
  )
}
